import { useParams, useLoaderData } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";
import { Skeleton } from "primereact/skeleton";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';
import FamilyTopNavbar from "./components/FamilyTopNavbar";
import accountsAPI from "../../services/accounts";
import RegistrationCard from "./components/RegistrationCard";
import FamilyStudentList from "./components/FamilyStudentList";
import AddNewRegistration from "./modals/AddNewRegistration";
import EditRegistration from "./modals/EditRegistration";
import EditRepresentativesModal from "./modals/EditRepresentativesModal";

export function FamilyTrainingPage() {
  const { school, training } = useLoaderData();
  const params = useParams();
  const schoolId = params.schoolId;
  const trainingId = params.trainingId;
  
  const [familyData, setFamilyData] = useState(null);
  const [representative1, setRepresentative1] = useState(null);
  const [representative2, setRepresentative2] = useState(null);
  const [parentsDivorced, setParentsDivorced] = useState(false);
  const [contactPerson1, setContactPerson1] = useState(null);
  const [contactPerson2, setContactPerson2] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [agenda, setAgenda] = useState([]);
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [showAllUpcoming, setShowAllUpcoming] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSeeMore, setShowSeeMore] = useState(false);
  const [refresh, setRefresh] = useState(new Date());
  const canEditInformations = training?.registration_allow_edit_informations !== false;
  
  const editRepresentativesModalRef = useRef(null);
  const addNewRegistrationRef = useRef(null);
  const editRegistrationRef = useRef(null);
  const messageContentRef = useRef(null);
  const toast = useRef(null);

  useEffect(() => {
    const fetchFamilyData = async () => {
      try {
        setLoading(true);
        const response = await accountsAPI.GetCurrentAccountFamily(schoolId);
        
        if (response.success) {
          setFamilyData(response.data);
          
          // Find the specific training from registrations
          const trainingRegistration = response.data.registrations.find(
            reg => reg.training.id === trainingId
          );
          
          if (trainingRegistration) {
            setRepresentative1(response.data.member);
            setRepresentative2(response.data.member); // Assuming single parent for now
            setParentsDivorced(false);
            setContactPerson1(response.data.contact_person_1);
            setContactPerson2(response.data.contact_person_2);
            setRegistrations(trainingRegistration.students || []);
            setContracts(response.data.contracts || []);
            setAgenda(response.data.agenda || []);
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Error fetching family data:", err);
        toast.current?.show({ 
          severity: 'error', 
          summary: 'Erreur de chargement', 
          detail: err?.message || 'Une erreur est survenue lors du chargement des données.', 
          life: 5000 
        });
        setLoading(false);
      }
    };

    fetchFamilyData();
  }, [schoolId, trainingId, refresh]);

  // Check if content overflows to show/hide "Voir plus" button
  useEffect(() => {
    const checkOverflow = () => {
      if (messageContentRef.current) {
        const element = messageContentRef.current;
        const isOverflowing = element.scrollHeight > element.clientHeight;
        setShowSeeMore(isOverflowing);
      }
    };

    if (training?.registration_message) {
      setTimeout(checkOverflow, 100);
    }
  }, [training?.registration_message, expanded]);

  const acceptConfirmaRegistration = (event, student) => {
    // TODO: Implement confirmation API call
    console.log("Confirm registration:", student);
    setRefresh(new Date());
    editRegistrationRef.current?.show(student, "Vous avez confirmé l'inscription. Merci de vérifier et/ou mettre à jour les informations si nécessaire.", "success");
  };

  const acceptUnregisterRegistration = (event, student) => {
    // TODO: Implement unregistration API call
    console.log("Unregister registration:", student);
    setRefresh(new Date());
    editRegistrationRef.current?.show(student, "Veuillez nous laisser un message pour nous en dire plus sur les raisons de votre décision.", "warn");
  };

  const confirmRegistration = (event, student) => {
    confirmPopup({
      target: event.currentTarget,
      message: 'Voulez-vous vraiment confirmer l\'inscription ?',
      icon: 'pi pi-exclamation-triangle',
      defaultFocus: 'accept',
      acceptLabel: 'Confirmer',
      rejectLabel: 'Annuler',
      acceptClassName: 'p-button-success',
      accept: (event) => {
        acceptConfirmaRegistration(event, student);
      }
    });
  };

  const unregisterRegistration = (event, student) => {
    confirmPopup({
      target: event.currentTarget,
      message: 'Voulez-vous vraiment désinscrire l\'inscription ?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Confirmer',
      rejectLabel: 'Annuler',
      acceptClassName: 'p-button-danger',
      accept: (event) => {
        acceptUnregisterRegistration(event, student);
      }
    });
  };

  if (loading) {
    return (
      <div style={{ height: "100vh" }}>
        <div className="min-h-screen flex flex-column">
         
          <div className="flex flex-column flex-auto p-3">
            <div className="flex flex-column align-items-center w-full h-full">
              <div className="w-full h-full" style={{ maxWidth: '990px' }}>
                <div className="flex flex-column lg:flex-row gap-4">
                  <div className="flex-none lg:w-3 w-full">
                    <Card>
                      <div className="flex flex-column align-items-center p-2">
                        <Skeleton height="200px" width="200px" className="mb-3" />
                        <Skeleton height="1.5rem" className="mb-2" />
                        <Skeleton height="1rem" width="80%" />
                      </div>
                    </Card>
                  </div>
                  <div className="flex-1 w-full">
                    <Skeleton height="2rem" className="mb-4" />
                    <Skeleton height="1rem" className="mb-2" />
                    <Skeleton height="1rem" width="60%" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: "100vh" }}>
      <div className="min-h-screen flex flex-column">
        
        <div className="flex flex-column flex-auto p-3">
          <div className="flex flex-column align-items-center w-full h-full">
            <div className="w-full h-full" style={{ maxWidth: '990px' }}>
              <div className="flex flex-column lg:flex-row gap-4">
                <div className="flex-none lg:w-3 w-full">
                  <Card>
                    <div className="flex flex-column align-items-center p-2">
                      <img src={school?.avatar || "/images/school-empty-logo.svg"} alt="School Logo" className="w-8" style={{ maxWidth: '200px' }} />
                      <span className="text-xl font-medium mt-3 text-center block">{school?.name}</span>
                      <span className="text-sm text-500 mt-3">{school?.presentation}</span>
                      <div className="text-center mt-3">
                        <hr className="w-6" />
                        <div className="text-sm text-500 mt-2">
                          <div>{school?.address}</div>
                          <div>{school?.city}, {school?.postal_code}</div>
                          <div>{school?.phone}</div>
                          <div>{school?.email}</div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
                <div className="flex-1 w-full">
                  <div>
                    <div className="text-3xl font-bold text-900 mb-4 text-center bg-gradient-to-r from-gray-50 to-gray-100 text-gray-800 p-4 border-round shadow-2 border-2 border-gray-200" style={{ 
                      background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                      textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                      letterSpacing: '0.5px'
                    }}>
                      {training?.name}
                    </div>
                  </div>

                  {/* Message de l'école */}
                  {training?.registration_message && (
                    <>
                      <div className="flex flex-column mb-4 mt-6">
                        <div className="text-lg font-bold mea-gradient-text">Message de l'école</div>
                      </div>
                      <div className="surface-section mt-4 px-2 py-2 md:px-3 lg:px-3">
                        <div className="mb-1 mt-2">
                          <div 
                            ref={messageContentRef}
                            style={{ maxHeight: expanded ? 'none' : '250px', overflow: 'hidden', position: 'relative' }}
                            dangerouslySetInnerHTML={{ __html: training?.registration_message }}>
                          </div>
                          {!expanded && training?.registration_message?.length > 0 && showSeeMore && (
                            <div className="text-center mt-3">
                              <Button label="Voir plus" text outlined onClick={() => setExpanded(true)} />
                            </div>
                          )}
                          {expanded && (
                            <div className="text-center mt-3">
                              <Button label="Voir moins" text outlined onClick={() => setExpanded(false)} />
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Agenda */}
                  {agenda && agenda.length > 0 && (
                    <>
                      <div className="flex flex-column mb-4 mt-6">
                        <div className="text-lg font-bold mea-gradient-text">Agenda</div>
                      </div>
                      <div className="bg-white border-round border-1 border-300 overflow-hidden">
                        <div className="flex flex-column p-4">
                          {(() => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            
                            // Only show events with these types (exclude COURSE_SESSION and other internal event types)
                            const visibleEventTypes = ['MEETING', 'VACATION', 'BANK_HOLIDAY', 'OTHER'];
                            const visibleEvents = agenda.filter(event => visibleEventTypes.includes(event.event_type));
                            
                            // Filter and sort events
                            const upcomingEvents = visibleEvents.filter(event => new Date(event.start_date) >= today)
                              .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
                            
                            const pastEvents = visibleEvents.filter(event => new Date(event.start_date) < today)
                              .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
                            
                            // Determine which upcoming events to show based on showAllUpcoming state
                            const upcomingToShow = showAllUpcoming ? upcomingEvents : upcomingEvents.slice(0, 3);
                            
                            // If showing past events, combine them with the current upcoming events view
                            const eventsToShow = showPastEvents ? 
                              [...pastEvents, ...upcomingToShow].sort((a, b) => new Date(a.start_date) - new Date(b.start_date)) : 
                              upcomingToShow;
                            
                            return (
                              <>
                                {eventsToShow.map((event, index) => {
                                  const isPastEvent = new Date(event.start_date) < today;
                                  
                                  // Determine colors based on event type
                                  const getEventColors = (eventType) => {
                                    switch (eventType) {
                                      case 'MEETING':
                                        return { bar: 'bg-purple-400', bg: 'bg-purple-100' };
                                      case 'VACATION':
                                      case 'BANK_HOLIDAY':
                                        return { bar: 'bg-green-400', bg: 'bg-green-100' };
                                      default:
                                        return { bar: 'bg-blue-400', bg: 'bg-blue-100' };
                                    }
                                  };
                                  
                                  const colors = isPastEvent ? { bar: 'bg-gray-300', bg: 'bg-gray-200' } : getEventColors(event.event_type);
                                  
                                  return (
                                    <div key={event.id}>
                                      <div className="flex align-items-start gap-4 py-3">
                                        {/* Date Block */}
                                        <div className="flex flex-column align-items-center" style={{ minWidth: '80px' }}>
                                          <div className="relative">
                                            <div className={`w-2 h-8 border-round ${colors.bar}`} style={{ 
                                              position: 'absolute', 
                                              left: '-8px', 
                                              top: '0' 
                                            }}></div>
                                            <div className={`border-round p-3 text-center ${colors.bg}`} style={{ minWidth: '60px' }}>
                                              <div className={`text-lg font-bold ${isPastEvent ? 'text-600' : 'text-900'}`}>
                                                {new Date(event.start_date).getDate()}
                                              </div>
                                              <div className={`text-xs font-medium ${isPastEvent ? 'text-500' : 'text-600'}`}>
                                                {new Date(event.start_date).toLocaleDateString("fr-FR", { month: 'short' })}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                        
                                        {/* Event Information */}
                                        <div className="flex-1">
                                          <div className="flex flex-column gap-1">
                                            <div className={`font-bold text-lg ${isPastEvent ? 'text-600' : 'text-900'}`}>{event.name}</div>
                                            <div className={`text-sm ${isPastEvent ? 'text-500' : 'text-900'}`}>
                                              {(() => {
                                                const startDate = new Date(event.start_date);
                                                const endDate = new Date(event.end_date);
                                                
                                                // Check if it's the same day
                                                const isSameDay = startDate.toDateString() === endDate.toDateString();
                                                
                                                if (isSameDay) {
                                                  return `Le ${startDate.toLocaleDateString("fr-FR", {
                                                    weekday: 'long',
                                                    day: 'numeric',
                                                    month: 'long'
                                                  })}`;
                                                } else {
                                                  return `Du ${startDate.toLocaleDateString("fr-FR", {
                                                    weekday: 'long',
                                                    day: 'numeric',
                                                    month: 'long'
                                                  })} au ${endDate.toLocaleDateString("fr-FR", {
                                                    weekday: 'long',
                                                    day: 'numeric',
                                                    month: 'long'
                                                  })}`;
                                                }
                                              })()}
                                            </div>
                                            <div className={`text-sm ${isPastEvent ? 'text-400' : 'text-600'}`}>
                                              {event.event_type === 'MEETING' ? 'Réunion' :
                                               event.event_type === 'VACATION' ? 'Vacances' :
                                               event.event_type === 'BANK_HOLIDAY' ? 'Jours fériés' : 'Événement'}
                                            </div>
                                            {event.presentation && (
                                              <div className={`text-sm mt-2 p-2 border-round ${isPastEvent ? 'text-400 bg-gray-100' : 'text-500 bg-gray-50'}`}>
                                                {event.presentation}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      {index < eventsToShow.length - 1 && (
                                        <div className="border-top-1 border-gray-200"></div>
                                      )}
                                    </div>
                                  );
                                })}
                                
                                {/* Show more/less buttons */}
                                <div className="flex justify-content-center gap-2 mt-4">
                                  {!showAllUpcoming && upcomingEvents.length > 3 && (
                                    <Button 
                                      label={`Voir plus d'événements (${upcomingEvents.length - 3} restants)`}
                                      text 
                                      size="small"
                                      onClick={() => setShowAllUpcoming(true)}
                                    />
                                  )}
                                  {showAllUpcoming && upcomingEvents.length > 3 && (
                                    <Button 
                                      label="Voir moins d'événements"
                                      text 
                                      size="small"
                                      onClick={() => setShowAllUpcoming(false)}
                                    />
                                  )}
                                  {!showPastEvents && pastEvents.length > 0 && (
                                    <Button 
                                      label={`Voir les événements passés (${pastEvents.length})`}
                                      text 
                                      size="small"
                                      onClick={() => setShowPastEvents(true)}
                                    />
                                  )}
                                  {showPastEvents && (
                                    <Button 
                                      label="Masquer les événements passés"
                                      text 
                                      size="small"
                                      onClick={() => setShowPastEvents(false)}
                                    />
                                  )}
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Responsables légaux */}
                  <div className="flex flex-column mb-4 mt-6">
                    <div className="text-lg font-bold mea-gradient-text">Responsables légaux</div>
                  </div>
                  <div className="bg-white border-round border-1 border-300 overflow-hidden">
                    <div className="grid m-0 p-0" style={{minHeight: '200px'}}>
                      <div className="col-12 lg:col-8 p-3 bg-gray-50 flex flex-column" style={{minHeight: '200px'}}>
                        <div className="grid gap-0 flex-1">
                          <div className="col-12 md:col-6">
                            <div className="flex flex-column gap-2 h-full">
                              <h6 className="text-sm font-bold text-700 m-0 flex align-items-center gap-2 mb-2 mea-gradient-text">
                                <i className="pi pi-user text-xs"></i>
                                Père
                              </h6>
                              <div className="p-3 bg-white border-round border-1 border-gray-200 flex-1">
                                <div className="grid gap-2">
                                  <div className="col-12">
                                    <div className="flex flex-column gap-0">
                                      <span className="text-600 text-xs font-medium">Nom complet</span>
                                      <span className="text-900 font-bold text-sm">{(representative1?.lastname || "") + " " + (representative1?.firstname || "")}</span>
                                    </div>
                                  </div>
                                  <div className="col-6">
                                    <div className="flex flex-column gap-0">
                                      <span className="text-600 text-xs font-medium">Email</span>
                                      <span className="text-900 font-bold text-sm">{representative1?.email || "-"}</span>
                                    </div>
                                  </div>
                                  <div className="col-6">
                                    <div className="flex flex-column gap-0">
                                      <span className="text-600 text-xs font-medium">Téléphone</span>
                                      <span className="text-900 font-bold text-sm">{representative1?.phone || "-"}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="col-12 md:col-6">
                            <div className="flex flex-column gap-2 h-full">
                              <h6 className="text-sm font-bold text-700 m-0 flex align-items-center gap-2 mb-2 mea-gradient-text">
                                <i className="pi pi-user text-xs"></i>
                                Mère
                              </h6>
                              <div className="p-3 bg-white border-round border-1 border-gray-200 flex-1">
                                <div className="grid gap-2">
                                  <div className="col-12">
                                    <div className="flex flex-column gap-0">
                                      <span className="text-600 text-xs font-medium">Nom complet</span>
                                      <span className="text-900 font-bold text-sm">{(representative2?.lastname || "") + " " + (representative2?.firstname || "")}</span>
                                    </div>
                                  </div>
                                  <div className="col-6">
                                    <div className="flex flex-column gap-0">
                                      <span className="text-600 text-xs font-medium">Email</span>
                                      <span className="text-900 font-bold text-sm">{representative2?.email || "-"}</span>
                                    </div>
                                  </div>
                                  <div className="col-6">
                                    <div className="flex flex-column gap-0">
                                      <span className="text-600 text-xs font-medium">Téléphone</span>
                                      <span className="text-900 font-bold text-sm">{representative2?.phone || "-"}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="mt-3">
                          <Button 
                            label="Modifier" 
                            icon="pi pi-pencil"
                            className="w-full"
                            size="small"
                            text raised
                            disabled={!canEditInformations}
                            onClick={() => {
                              editRepresentativesModalRef.current?.show(representative1, representative2, parentsDivorced, contactPerson1, contactPerson2);
                            }}
                          />
                        </div>
                      </div>

                      <div className="col-12 lg:col-4 p-3" style={{minHeight: '200px'}}>
                        <div className="flex flex-column gap-3 h-full">
                          <div className="flex flex-column gap-2">
                            <h6 className="text-sm font-bold text-700 m-0 flex align-items-center gap-2 mb-2 mea-gradient-text">
                              <i className="pi pi-heart text-xs"></i>
                              Situation familiale
                            </h6>
                            <div className="p-3 bg-white border-round border-1 border-gray-200">
                              <div className="flex align-items-center gap-2">
                                <i className={`pi ${parentsDivorced ? 'pi-times-circle text-red-500' : 'pi-check-circle text-green-500'} text-sm`}></i>
                                <span className="text-900 font-bold text-sm">{parentsDivorced ? "Divorcés" : "Mariés"}</span>
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-column gap-2">
                            <h6 className="text-sm font-bold text-700 m-0 flex align-items-center gap-2 mb-2 mea-gradient-text">
                              <i className="pi pi-phone text-xs"></i>
                              Autres contacts
                            </h6>
                            <div className="flex flex-column gap-2">
                              <div className="p-2 bg-white border-round border-1 border-gray-200">
                                <div className="flex flex-column gap-0">
                                  <span className="text-600 text-xs font-medium">Personne 1</span>
                                  <span className="text-900 font-bold text-sm">{(contactPerson1?.name || "-") + " " + (contactPerson1?.phone || "")}</span>
                                </div>
                              </div>
                              <div className="p-2 bg-white border-round border-1 border-gray-200">
                                <div className="flex flex-column gap-0">
                                  <span className="text-600 text-xs font-medium">Personne 2</span>
                                  <span className="text-900 font-bold text-sm">{(contactPerson2?.name || "-") + " " + (contactPerson2?.phone || "")}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Élèves */}
                  <div className="flex flex-column mb-4 mt-6">
                    <div className="text-lg font-bold mea-gradient-text">Élèves</div>
                  </div>
                  <FamilyStudentList
                    students={registrations}
                    renderStudent={(student) => (
                      <RegistrationCard 
                        training={training} 
                        student={student} 
                        key={student?.id} 
                        editRegistrationRef={editRegistrationRef} 
                        confirmRegistration={confirmRegistration} 
                        unregisterRegistration={unregisterRegistration} 
                        canEditInformations={canEditInformations}
                      />
                    )}
                  />

                  {/* Paiements des cotisations */}
                  <div className="flex flex-column mb-4 mt-6">
                    <div className="text-lg font-bold mea-gradient-text">Paiements des cotisations</div>
                  </div>
                  <div className="bg-white border-round border-1 border-300 overflow-hidden surface-card px-4 py-3 md:px-6 lg:px-6 mt-4">
                    <div className="flex flex-column">
                      {training?.registration_allow_payments === false ? (
                        <div className="text-center py-4">
                          <i className="pi pi-info-circle text-3xl text-400 mb-2"></i>
                          <div className="text-500 text-sm">Les paiements des cotisations ne sont pas encore ouverts.</div>
                        </div>
                      ) : contracts && contracts?.length > 0 ? (
                        <div className="flex flex-column gap-3">
                          {contracts?.map((contract, index) => (
                            <div key={contract?.id || index} className="p-3 bg-gray-50 border-round border-1 border-gray-200">
                              <div className="grid">
                                <div className={`col-12 ${contract?.payment_status === "partial" ? "md:col-4" : "md:col-6"}`}>
                                  <div className="flex flex-column gap-1">
                                    <span className="text-500 text-sm">Montant total</span>
                                    <span className="font-medium text-lg">€{contract?.total_amount || "0.00"}</span>
                                  </div>
                                </div>
                                <div className={`col-12 ${contract?.payment_status === "partial" ? "md:col-4" : "md:col-6"}`}>
                                  <div className="flex flex-column gap-1">
                                    <span className="text-500 text-sm">Statut du paiement</span>
                                    <div className="flex align-items-center gap-2">
                                      {contract?.payment_status === "paid" && (
                                        <>
                                          <i className="pi pi-check-circle text-green-500"></i>
                                          <span className="font-medium text-green-600">Payé</span>
                                        </>
                                      )}
                                      {contract?.payment_status === "partial" && (
                                        <>
                                          <i className="pi pi-clock text-orange-500"></i>
                                          <span className="font-medium text-orange-600">Partiellement payé</span>
                                        </>
                                      )}
                                      {contract?.payment_status === "pending" && (
                                        <>
                                          <i className="pi pi-hourglass text-yellow-500"></i>
                                          <span className="font-medium text-yellow-600">En attente de paiement</span>
                                        </>
                                      )}
                                      {!contract?.payment_status && (
                                        <>
                                          <i className="pi pi-question-circle text-gray-500"></i>
                                          <span className="font-medium text-gray-600">Non défini</span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                {contract?.payment_status === "partial" && (
                                  <div className="col-12 md:col-4">
                                    <div className="flex flex-column gap-1">
                                      <span className="text-500 text-sm">Montant payé</span>
                                      <span className="font-medium text-lg text-orange-600">€{contract?.paid_balance || "0.00"}</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className="mt-3 pt-3 border-top-1 border-gray-200">
                                <div className="flex align-items-center gap-2">
                                  <i className="pi pi-file text-400"></i>
                                  <span className="text-500 text-sm">Contrat {contract?.id}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <i className="pi pi-credit-card text-3xl text-400 mb-2"></i>
                          <div className="text-500 text-sm">Aucun contrat de paiement</div>
                          <div className="text-400 text-xs mt-1">Les contrats seront bientôt disponibles</div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Nouvelle inscription */}
                  {training?.registration_allow_new && (
                    <div className="card mt-6">
                      <div className="flex flex-column align-items-center p-4">
                        <h3 className="text-lg font-bold mt-0 mb-3">Ajouter une nouvelle inscription</h3>
                        <p className="text-500 text-center mb-4">
                          Vous pouvez faire une demande d'inscription pour un autre enfant en utilisant les mêmes informations de contact
                        </p>
                        <Button 
                          size="large" 
                          icon="pi pi-plus" 
                          loading={loading} 
                          label="Nouvelle inscription" 
                          className="p-button p-component p-button-primary" 
                          onClick={() => {
                            addNewRegistrationRef.current?.show();
                          }} 
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <EditRepresentativesModal 
        ref={editRepresentativesModalRef} 
        school={school} 
        training={training} 
        callbackHandler={(father, mother, parentsDivorced, contactPerson1, contactPerson2) => {
          setLoading(true);
          // TODO: Implement update contacts API call
          console.log("Update contacts:", { father, mother, parentsDivorced, contactPerson1, contactPerson2 });
          setRefresh(new Date());
          setLoading(false);
        }} 
      />
      <AddNewRegistration 
        ref={addNewRegistrationRef} 
        school={school} 
        training={training} 
        father={representative1} 
        mother={representative2} 
        callbackHandler={(student) => {
          setLoading(true);
          // TODO: Implement new registration API call
          console.log("New registration:", student);
          setRefresh(new Date());
          setLoading(false);
        }} 
      />
      <EditRegistration 
        ref={editRegistrationRef} 
        school={school} 
        father={representative1} 
        mother={representative2} 
        training={training} 
        onDelete={(student) => {
          setLoading(true);
          // TODO: Implement delete registration API call
          console.log("Delete registration:", student);
          setRefresh(new Date());
          setLoading(false);
        }}
        callbackHandler={(student) => {
          setLoading(true);
          // TODO: Implement update registration API call
          console.log("Update registration:", student);
          setRefresh(new Date());
          setLoading(false);
        }} 
      />

      <Toast ref={toast} />
      <ConfirmPopup />
    </div>
  );
}
